import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Table, message, Spin } from "antd";
import { baseUrl } from "../config";
import api from "../utils/api";

const GenericTable = forwardRef(function GenericTable(
  { apiPath, columns, filterOptions = [] },
  ref
) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // ✅ Renamed to activeFilter to handle both Key and Value
  const [activeFilter, setActiveFilter] = useState(
    filterOptions.length > 0 ? filterOptions[0] : null
  );

  const loaderRef = useRef(null);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const LIMIT = 20;

  const fetchMore = useCallback(async (reset = false) => {
    if (loadingRef.current) return;

    const currentOffset = reset ? 0 : offsetRef.current;

    loadingRef.current = true;
    setLoading(true);

    try {
      const params = { offset: currentOffset, limit: LIMIT };

      // ✅ Check if activeFilter exists and is not the "All" option
      if (activeFilter && activeFilter.filterParam !== "all" && activeFilter.value !== null) {
        params[activeFilter.filterParam] = activeFilter.value;
      }

      const response = await api.get(baseUrl + apiPath, { params });
    const rows = Array.isArray(response.data) 
  ? response.data 
  : Object.values(response.data).find(val => Array.isArray(val)) || [];

      if (reset) {
        setData(rows);
      } else {
        setData(prev => [...prev, ...rows]);
      }

      offsetRef.current = currentOffset + rows.length;
      setHasMore(rows.length === LIMIT);
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load data");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [apiPath, activeFilter]);

  // ✅ FIX: Changed 'filterValue' to 'activeFilter' to match state
  useEffect(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setData([]);
    fetchMore(true);
  }, [activeFilter, apiPath]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
          fetchMore(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [fetchMore, hasMore]);

  // Exposed to parent components via ref — lets a parent patch a row's
  // fields in local state instantly, with no network refetch and no
  // full-table reload flicker.
  useImperativeHandle(ref, () => ({
    updateRow: (id, patch) => {
      setData((prev) =>
        prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
      );
    },
    reload: () => fetchMore(true), // still available if a full refresh is ever needed
  }), [fetchMore]);

  return (
    <>
      {/* Filter Buttons */}
      {filterOptions.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
          {/* ✅ FIX: Destructured 'option' incorrectly before. Changed to just 'option' */}
          {filterOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setActiveFilter(option)}
              style={{
                padding: "4px 16px",
                borderRadius: 6,
                border: "1px solid #d9d9d9",
                background: activeFilter?.label === option.label ? "#163968" : "#fff",
                color: activeFilter?.label === option.label ? "#fff" : "#000",
                cursor: "pointer",
                fontWeight: activeFilter?.label === option.label ? 600 : 400,
              }}
            >
              {option.label} {/* ✅ FIX: Using option.label instead of undefined 'label' */}
            </button>
          ))}
        </div>
      )}

      <Table
        rowKey="id"
        dataSource={data}
        columns={typeof columns === 'function' ? columns(() => fetchMore(true)) : columns}
        pagination={false}
        loading={loading}
      />

      {hasMore && (
        <div ref={loaderRef} style={{ textAlign: "center", padding: 24 }}>
          {loading && <Spin />}
        </div>
      )}

      {!hasMore && data.length > 0 && (
        <div style={{ textAlign: "center", padding: 16, color: "#aaa" }}>
          No more records
        </div>
      )}
    </>
  );
});

export default GenericTable;